
import { useQuery } from "@tanstack/react-query";
import { fetchStudentKpi, StudentKpi } from "@/services/studentKpiService";
import { devLog } from "@/utils/devLog";
import { useEffect } from "react";

export function useStudentKpi(creatorId: string | undefined, periodDays: number = 30) {
  // Ensure periodDays is a number
  const periodDaysNumber = typeof periodDays === 'string' ? parseInt(periodDays, 10) : periodDays;
  
  useEffect(() => {
    devLog(`[useStudentKpi] Hook initialized with creatorId: ${creatorId}, periodDays: ${periodDays}`);
    return () => devLog('[useStudentKpi] Hook unmounting');
  }, [creatorId, periodDays]);

  return useQuery<StudentKpi, Error>({
    queryKey: ["studentKpis", creatorId, periodDaysNumber],
    queryFn: async () => {
      if (!creatorId) {
        throw new Error("Creator ID is required");
      }
      
      devLog(`[KPI] Fetching KPIs for creator ${creatorId} with period ${periodDaysNumber}`);
      console.log(`[useStudentKpi] Fetching KPIs for creator ${creatorId} with period ${periodDaysNumber}`);
      
      try {
        const { data, error } = await fetchStudentKpi(creatorId, periodDaysNumber);
        
        if (error) {
          devLog("[KPI] Error fetching KPIs:", error);
          console.error("[useStudentKpi] Error details:", error);
          throw new Error(typeof error === 'string' ? error : 'Failed to fetch student KPIs');
        }
        
        devLog("[KPI] Successfully received KPI data:", data);
        console.log("[useStudentKpi] Successfully received data:", data);
        return data;
      } catch (error) {
        devLog("[KPI] Exception in KPI fetch:", error);
        console.error("[useStudentKpi] Exception details:", error);
        throw error;
      }
    },
    enabled: !!creatorId,
    retry: 2,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    meta: { // Use meta instead of onError based on latest @tanstack/react-query
      onError: (error: Error) => {
        console.error("[useStudentKpi] Query error:", error);
      }
    }
  });
}
